import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ObliqueIcon from "@/components/icons/ObliqueIcon";

/**
 * BreadcrumbNav - Ultra-refined, Google Drive inspired Breadcrumb Navigation
 * Features:
 * 1. Automatic ancestor collapse to '...' when 4 or more items are present.
 * 2. Dropdown for '...' listing hidden parent folders/routes for instant backtracking.
 * 3. Native dropdown caret '▾' on 'Interactions' (or any item with menuItems) offering
 *    seamless switching between Calls, Emails, Chats, Leads, and Meetings.
 * 4. Silent, zero-bloat styling that strictly maintains the 12px text height.
 */
export default function BreadcrumbNav({ items = [], className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null); // 'ellipsis' | item-index
  const containerRef = useRef(null);

  // Close menus on outside click or Escape
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (!items || items.length === 0) return null;

  // Determine active interaction channel from current path & search params
  const currentPath = location.pathname + location.search;
  const isEmails = currentPath.includes("type=emails") || location.pathname.includes("/quality/emails");
  const isChats = currentPath.includes("type=chats") || location.pathname.includes("/quality/chats");
  const isVideos = currentPath.includes("type=videos") || location.pathname.includes("/quality/meetings");
  const isCalls = currentPath.includes("type=calls") || 
                  location.pathname.includes("/quality/calls") || 
                  (location.pathname === "/quality/audits" && !isEmails && !isChats && !isVideos && !currentPath.includes("type=leads"));

  const defaultInteractionMenu = [
    {
      id: "calls",
      label: "Call Audits",
      sublabel: "Phone & Voice Recordings",
      icon: "call",
      path: "/quality/audits?type=calls",
      isActive: isCalls,
    },
    {
      id: "meetings",
      label: "Meeting Audits",
      sublabel: "Google Meet Video Recordings",
      icon: "videocam",
      path: "/quality/audits?type=videos",
      isActive: isVideos,
    },
    {
      id: "emails",
      label: "Email Audits",
      sublabel: "Email Outreach QA",
      icon: "mail",
      path: "/quality/audits?type=emails",
      isActive: isEmails,
    },
    {
      id: "chats",
      label: "Chat Audits",
      sublabel: "Live Chat Support",
      icon: "chat",
      path: "/quality/audits?type=chats",
      isActive: isChats,
    },
  ];

  // Ellipsis collapsing only if path is exceptionally deep (5+ items)
  const shouldCollapse = items.length >= 6;
  const collapsedAncestors = shouldCollapse ? items.slice(0, items.length - 2) : [];
  const visibleItems = shouldCollapse ? items.slice(items.length - 2) : items;

  // Determine if this breadcrumb represents the Interactions QA section
  const hasInteractionsAncestor = items.some(i => 
    i.label?.toLowerCase().includes("interaction") || 
    i.label?.toLowerCase().includes("qms")
  );

  return (
    <div
      ref={containerRef}
      className={`flex items-center gap-1.5 text-xs text-[var(--text-tertiary)] font-medium select-none ${openMenu ? 'relative z-[3500]' : ''} ${className}`}
    >
      {/* ── Collapsed Ancestors Ellipsis Button (...) ───────────────────────── */}
      {shouldCollapse && (
        <div className={`relative inline-flex items-center ${openMenu === "ellipsis" ? 'z-[3500]' : ''}`}>
          <button
            type="button"
            onClick={() => setOpenMenu(openMenu === "ellipsis" ? null : "ellipsis")}
            title="Show parent locations"
            className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] border border-transparent hover:border-[var(--border)] transition-all cursor-pointer p-0 shrink-0"
          >
            <ObliqueIcon name="more_horiz" size={15} />
          </button>

          {/* Ellipsis Dropdown Popover */}
          {openMenu === "ellipsis" && (
            <div className="absolute top-full left-0 mt-1 min-w-[210px] bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl py-1.5 z-[3500] animate-in fade-in-0 zoom-in-95 duration-100">
              <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)] border-b border-[var(--border)] mb-1">
                Parent Location
              </div>
              {collapsedAncestors.map((ancestor, aIdx) => (
                <button
                  key={aIdx}
                  type="button"
                  onClick={() => {
                    setOpenMenu(null);
                    if (ancestor.onClick) ancestor.onClick();
                    else if (ancestor.path) navigate(ancestor.path);
                  }}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-primary)] hover:bg-[var(--bg-muted)] flex items-center gap-2.5 transition-colors cursor-pointer border-none bg-transparent"
                >
                  <ObliqueIcon
                    name={ancestor.icon || (aIdx === 0 ? "domain" : "folder")}
                    size={14}
                    className="text-[var(--text-secondary)] shrink-0"
                  />
                  <span className="font-medium truncate">{ancestor.label}</span>
                </button>
              ))}
            </div>
          )}

          <ObliqueIcon name="chevron_right" size={12} className="text-[var(--text-tertiary)] ml-1" />
        </div>
      )}

      {/* ── Visible Breadcrumb Items ────────────────────────────────────────── */}
      {visibleItems.map((item, idx) => {
        const isLast = idx === visibleItems.length - 1;
        
        // Dropdown belongs under the active leaf audit item (e.g. "Lead Audits ▾" or "Call Audits ▾")
        const isAuditLeaf = isLast && (
          hasInteractionsAncestor ||
          item.label?.toLowerCase().includes("audit") ||
          item.hasDropdown
        );
        const hasCustomMenu = item.hasDropdown || Boolean(item.menuItems?.length);
        const hasMenu = isAuditLeaf || hasCustomMenu;
        const menuKey = `item-${idx}`;
        const isItemMenuOpen = openMenu === menuKey;

        const menuOptions = item.menuItems || (isAuditLeaf ? defaultInteractionMenu : []);

        return (
          <React.Fragment key={idx}>
            <div className={`relative inline-flex items-center ${isItemMenuOpen ? 'z-[3500]' : ''}`}>
              {hasMenu ? (
                <div
                  onClick={() => setOpenMenu(isItemMenuOpen ? null : menuKey)}
                  className={`inline-flex items-center gap-1 px-2 py-1 -mx-1 rounded-lg transition-all cursor-pointer group select-none ${
                    isItemMenuOpen
                      ? "bg-[var(--accent-subtle)] text-[var(--accent)] font-semibold shadow-2xs"
                      : "hover:bg-[var(--bg-muted)] text-[var(--text-primary)] font-semibold"
                  }`}
                >
                  <span className="truncate">{item.label}</span>
                  <ObliqueIcon
                    name="expand_more"
                    size={14}
                    className={`transition-transform duration-200 text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)] ${
                      isItemMenuOpen ? "rotate-180 text-[var(--accent)]" : ""
                    }`}
                  />
                </div>
              ) : (
                <span
                  onClick={item.onClick || (item.path ? () => navigate(item.path) : undefined)}
                  className={`transition-colors truncate px-1 py-0.5 rounded-md ${
                    (item.onClick || item.path)
                      ? "cursor-pointer hover:text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]"
                      : "cursor-default text-[var(--text-tertiary)]"
                  } ${
                    isLast
                      ? "text-[var(--text-primary)] font-semibold"
                      : "text-[var(--text-secondary)] font-normal"
                  }`}
                >
                  {item.label}
                </span>
              )}

              {/* Aesthetic Item Dropdown Popover (Matching Filter & Sort Notion-style design) */}
              {hasMenu && isItemMenuOpen && (
                <div className="absolute top-full left-0 mt-2 min-w-[220px] bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)] p-1 z-[3500] animate-in fade-in-0 zoom-in-95 duration-150">
                  <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] border-b border-[var(--border-subtle)] mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <ObliqueIcon name="swap_horiz" size={13} color="var(--text-tertiary)" />
                      {item.dropdownTitle || "Interaction Channel"}
                    </span>
                    <span className="text-[10px] text-[var(--text-tertiary)] font-normal lowercase tracking-normal">switch</span>
                  </div>
                  
                  <div className="flex flex-col gap-0.5 max-h-[320px] overflow-y-auto custom-scrollbar">
                    {menuOptions.map((opt) => (
                      <button
                        key={opt.id || opt.label}
                        type="button"
                        onClick={() => {
                          setOpenMenu(null);
                          if (opt.onClick) opt.onClick();
                          else if (opt.path) navigate(opt.path);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-md text-[13px] flex items-center justify-between transition-colors duration-150 cursor-pointer border-none bg-transparent ${
                          opt.isActive
                            ? "bg-[var(--bg-subtle)] text-[var(--text-primary)] font-medium"
                            : "hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex items-center justify-center shrink-0 w-4 h-4">
                            <ObliqueIcon 
                              name={opt.icon || "radio_button_checked"} 
                              size={15} 
                              color={opt.isActive ? "var(--accent)" : "var(--text-secondary)"} 
                            />
                          </div>
                          <span className="truncate leading-none">{opt.label}</span>
                        </div>
                        {opt.isActive && (
                          <ObliqueIcon name="check" size={14} className="text-[var(--accent)] shrink-0 ml-2" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {!isLast && <ObliqueIcon name="chevron_right" size={12} className="text-[var(--text-tertiary)]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
